import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  BarChart3,
  TrendingUp,
  Star,
  Activity,
  Eye
} from 'lucide-react';
import { TabLayout, ContentCard } from '../../components/ui/TabLayout';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Filter } from '../../components/ui/Filter';

export function DriverPerformancePage() {
  const { drivers } = useSelector(state => state.drivers);
  const [activeTab, setActiveTab] = useState('overview');
  const [filter, setFilter] = useState('');

  const performanceColumns = [
    {
      key: 'name',
      header: 'Driver Name',
      primary: true,
      render: (_, item) => {
        const name = item?.name || `${item?.personalInfo?.firstName || ''} ${item?.personalInfo?.lastName || ''}`.trim();
        const email = item?.email || item?.personalInfo?.email || '';
        return (
          <div>
            <div className="font-medium text-gray-900">{name}</div>
            <div className="text-sm text-gray-500">{email}</div>
          </div>
        );
      }
    },
    {
      key: 'rating',
      header: 'Rating',
      render: (value) => (
        <div className="flex items-center">
          <Star size={16} className="text-yellow-400 mr-1" />
          <span className="font-medium">{value?.toFixed(1) || 'N/A'}</span>
        </div>
      )
    },
    {
      key: 'totalTrips',
      header: 'Total Trips',
      render: (value) => value?.toLocaleString() || 0
    },
    {
      key: 'completionRate',
      header: 'Completion Rate',
      render: (value) => (
        <StatusBadge 
          status={`${value?.toFixed(1) || 0}%`}
          variant={
            (value || 0) >= 95 ? 'success' :
            (value || 0) >= 85 ? 'warning' : 'error'
          }
        />
      )
    },
    {
      key: 'totalEarnings',
      header: 'Total Earnings',
      render: (value) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value || 0)
    },
    {
      key: 'lastActive',
      header: 'Last Active',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    }
  ];

  const performanceActions = [
    {
      type: 'button',
      onClick: (item) => console.log('View performance details:', item),
      label: 'Details',
      variant: 'secondary',
      icon: Eye
    }
  ];

  const getFilteredDrivers = () => {
    return drivers.filter(driver => {
      const name = driver?.name || `${driver?.personalInfo?.firstName || ''} ${driver?.personalInfo?.lastName || ''}`.trim();
      const email = driver?.email || driver?.personalInfo?.email || '';
      const matchesFilter = name.toLowerCase().includes(filter.toLowerCase()) ||
                           email.toLowerCase().includes(filter.toLowerCase());
      
      switch (activeTab) {
        case 'top-performers':
          return matchesFilter && ((driver.rating || 0) >= 4.5 && (driver.completionRate || 0) >= 95);
        case 'needs-attention':
          return matchesFilter && ((driver.rating || 0) < 4.0 || (driver.completionRate || 0) < 85);
        default:
          return matchesFilter;
      }
    });
  };

  const filteredDrivers = getFilteredDrivers();

  const tabs = [
    { id: 'overview', label: 'Performance Overview', icon: BarChart3 },
    { id: 'top-performers', label: 'Top Performers', icon: TrendingUp },
    { id: 'needs-attention', label: 'Needs Attention', icon: Activity }
  ];

  const getPerformanceStats = () => {
    const totalDrivers = drivers.length;
    const topPerformers = drivers.filter(d => (d.rating || 0) >= 4.5 && (d.completionRate || 0) >= 95).length;
    const needsAttention = drivers.filter(d => (d.rating || 0) < 4.0 || (d.completionRate || 0) < 85).length;
    const avgRating = totalDrivers > 0 ? drivers.reduce((sum, d) => sum + (d.rating || 0), 0) / totalDrivers : 0;
    const avgCompletionRate = drivers.reduce((sum, d) => sum + (d.completionRate || 0), 0) / totalDrivers;
    const totalEarnings = drivers.reduce((sum, d) => sum + (d.totalEarnings || 0), 0);

    return {
      totalDrivers,
      topPerformers,
      needsAttention,
      avgRating,
      avgCompletionRate,
      totalEarnings
    };
  };

  const stats = getPerformanceStats();

  return (
    <TabLayout
      title="Driver Performance"
      subtitle="Monitor and analyze driver performance metrics and analytics"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {/* Performance KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {stats.avgRating.toFixed(1)} ⭐
          </div>
          <p className="text-sm text-gray-600 mt-1">Average Rating</p>
        </ContentCard>
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {stats.avgCompletionRate.toFixed(1)}%
          </div>
          <p className="text-sm text-gray-600 mt-1">Avg Completion Rate</p>
        </ContentCard>
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-purple-600">
            {stats.topPerformers}
          </div>
          <p className="text-sm text-gray-600 mt-1">Top Performers</p>
        </ContentCard>
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-orange-600">
            {stats.needsAttention}
          </div>
          <p className="text-sm text-gray-600 mt-1">Needs Attention</p>
        </ContentCard>
      </div>

      {/* Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 max-w-md">
          <Filter
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or email..."
          />
        </div>
      </div>

      {/* Performance Table */}
      <ResponsiveTable
        data={filteredDrivers}
        columns={performanceColumns}
        actions={performanceActions}
        emptyState={
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No drivers in {activeTab}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              There are currently no drivers in the {activeTab} category.
            </p>
          </div>
        }
      />

      {/* Performance Insights */}
      <ContentCard title="Performance Insights">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
              }).format(stats.totalEarnings)}
            </div>
            <p className="text-sm text-gray-600 mt-1">Total Driver Earnings</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {drivers.reduce((sum, d) => sum + (d.totalTrips || 0), 0).toLocaleString()}
            </div>
            <p className="text-sm text-gray-600 mt-1">Total Trips Completed</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">
              {((stats.topPerformers / stats.totalDrivers) * 100).toFixed(1)}%
            </div>
            <p className="text-sm text-gray-600 mt-1">Top Performer Rate</p>
          </div>
        </div>
      </ContentCard>
    </TabLayout>
  );
}
